import {
  getPresignedUploadUrl,
  uploadFileToPresignedUrl,
} from '@/services/SignupService';
import { UserService } from '@/services/UserService';
import { RELATION_OPTIONS } from '@/utils/constant/constant';
import {
  addModalOpenClass,
  removeModalOpenClass,
  validateEmail,
} from '@/utils/helper';
import imageCompression from 'browser-image-compression';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { toast } from 'react-toastify';
import '../../../assets/styles/react-datepicker.css';
import './SubMemberEditModal.scss';

interface Member {
  id: number;
  first_name: string;
  last_name: string;
  relation: string;
  date_of_birth: string;
  photo: string;
  photo_key: string;
  email: string;
}

interface SubMemberEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  refresh: () => void;
  member: Member;
  isEdit: boolean;
}

const initialMember: Member = {
  id: 0,
  first_name: '',
  last_name: '',
  relation: '',
  date_of_birth: '',
  photo: '',
  photo_key: '',
  email: '',
};

const SubMemberEditModal: React.FC<SubMemberEditModalProps> = ({
  isOpen,
  onClose,
  refresh,
  member,
  isEdit,
}) => {
  const [error, setError] = useState<any>({});
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState<Member>(initialMember);
  const [isDuplicateEmail, setIsDuplicateEmail] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const [relationOptions, setRelationOptions] = useState(RELATION_OPTIONS);

  const handleFormChange = (e: any) => {
    setForm((prevForm) => ({ ...prevForm, [e.target.name]: e.target.value }));
  };

  // const html = document.documentElement;
  // html.classList.add('modal-open');

  const handleImageUpload = async (file: File) => {
    const prevPhotoKey = form.photo_key;
    const prevPhoto = form.photo;

    try {
      const name = `${Date.now()}_${file.name}`;
      const key = `images/${name}`;
      const presignedUrl = await getPresignedUploadUrl(key);

      if (!presignedUrl?.data?.data) {
        toast.error('Failed to get presigned URL.');
        return;
      }

      setIsImageUploading(true);

      setForm((prevForm) => ({
        ...prevForm,
        photo_key: name,
        photo: URL.createObjectURL(file),
      }));

      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        alwaysKeepResolution: true,
      };

      let compressedFile: File;
      try {
        compressedFile = await imageCompression(file, options);
      } catch (err) {
        setForm((prevForm) => ({
          ...prevForm,
          photo_key: prevPhotoKey,
          photo: prevPhoto,
        }));
        toast.error(`Image compression failed: ${err}`);
        return;
      }

      try {
        await uploadFileToPresignedUrl(presignedUrl.data.data, compressedFile);
      } catch (err) {
        setForm((prevForm) => ({
          ...prevForm,
          photo_key: prevPhotoKey,
          photo: prevPhoto,
        }));
        toast.error(`Image upload failed: ${err}`);
        return;
      }
    } catch (err) {
      setForm((prevForm) => ({
        ...prevForm,
        photo_key: prevPhotoKey,
        photo: prevPhoto,
      }));
      toast.error(`Unexpected error: ${err}`);
    } finally {
      setIsImageUploading(false);
    }
  };

  const removeImage = () => {
    setForm({ ...form, photo: '', photo_key: '' });
  };

  const validateSubMember = (): boolean => {
    let isValid = true;
    const temp: any = {};

    if (!form.photo_key?.trim()) {
      isValid = false;
      temp['photo_key'] = 'Photo is required';
    }

    if (!form.relation) {
      isValid = false;
      temp['relation'] = 'Relation is required';
    }

    if (!form.first_name?.trim()) {
      isValid = false;
      temp['first_name'] = 'First name is required';
    }

    if (!form.last_name?.trim()) {
      isValid = false;
      temp['last_name'] = 'Last name is required';
    }

    if (!form.date_of_birth) {
      isValid = false;
      temp['date_of_birth'] = 'Date of birth is required';
    }

    if (form.email?.trim() && !validateEmail(form.email)) {
      isValid = false;
      temp['email'] = 'Please enter a valid email address';
    }
    // else if (isDuplicateEmail) {
    //   isValid = false;
    //   temp['email'] = 'Email already exists';
    // }

    setError(temp);
    return isValid;
  };

  const handleClose = () => {
    onClose();
    setError({});
    setSubmitted(false);
    setForm(initialMember);
    setIsDuplicateEmail(false);
    removeModalOpenClass();
  };

  const checkForUpdate = (payload: Partial<Member>) => {
    return (
      payload.date_of_birth !== member?.date_of_birth ||
      payload.first_name?.trim() !== member?.first_name?.trim() ||
      payload.last_name?.trim() !== member?.last_name?.trim() ||
      payload.relation !== member?.relation ||
      payload.photo_key !== member?.photo_key ||
      payload.email !== member?.email
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isImageUploading) {
      return;
    }
    setSubmitted(true);
    if (!validateSubMember()) return;

    const payload: Partial<Member & { email: string }> = {
      date_of_birth: form.date_of_birth,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      relation: form.relation,
      photo_key: form.photo_key,
      email: form.email.trim(),
    };

    try {
      if (form.id) {
        if (checkForUpdate(payload)) {
          UserService.updateSubMember(form.id, payload).then((res) => {
            if (res?.data) {
              refresh();
              toast.success('Family member updated successfully.');
              handleClose();
            }
          });
        } else {
          handleClose();
        }
      } else {
        UserService.createSubMember(payload).then((res) => {
          if (res?.data) {
            refresh();
            toast.success('Family member created successfully.');
            handleClose();
          }
        });
      }
    } catch (error) {
      toast.error('Some error occurred while saving family member.');
    }
  };

  // const checkEmail = useCallback(
  //   _.debounce((email: string, id?: number) => {
  //     checkSubMemberDuplicateEmail(email, id).then((res: any) => {
  //       if (res?.data?.data?.isDuplicate) {
  //         setIsDuplicateEmail(true);
  //       } else {
  //         setIsDuplicateEmail(false);
  //         setError((prev: any) => ({ ...prev, email: '' }));
  //       }
  //     });
  //   }, 500),
  //   []
  // );

  useEffect(() => {
    if (isOpen) {
      addModalOpenClass();
      setForm(member);
      setRelationOptions(() => {
        return isEdit && member?.relation !== 'guest' ? RELATION_OPTIONS.filter((opt) => opt.value !== 'guest') : RELATION_OPTIONS;
      });
    }
  }, [isOpen, member]);

  useEffect(() => {
    validateSubMember();
  }, [form]);

  // useEffect(() => {
  //   if (form.email?.trim() && validateEmail(form.email?.trim())) {
  //     checkEmail(form.email.trim(), form.id);
  //   } else {
  //     setIsDuplicateEmail(false);
  //   }
  // }, [form.email]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-center">
        <div className="modal-wrapper" onClick={(e) => e.stopPropagation()}>
          <h2 className="modal-title">
            {isEdit ? 'Edit' : 'Add'} Family Member / Guest
          </h2>
          <form
            className="edit-modal-form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="edit-modal-grid">
              <div
                className={`upload-file form-group ${
                  submitted && error.photo_key ? 'error' : ''
                }`}
              >
                <div className="photo-upload">
                  {isImageUploading && (
                    <div className="uploading-img">
                      <div className="loader">
                        <svg className="circular-loader" viewBox="25 25 50 50">
                          <circle
                            className="loader-path"
                            cx="50"
                            cy="50"
                            r="20"
                            fill="none"
                            stroke="#70c542"
                            stroke-width="2.5"
                          />
                        </svg>
                      </div>
                      <span>Uploading...</span>
                    </div>
                  )}
                  {form.photo ? (
                    <div className="photo-preview">
                      <img src={form.photo} alt="Preview" />
                      {!isImageUploading && (
                        <>
                          <button
                            type="button"
                            className="remove-photo-btn"
                            onClick={removeImage}
                          >
                            &times;
                          </button>
                          <button
                            type="button"
                            className="remove-photo-btn"
                            style={{ marginTop: '33px' }}
                            onClick={() => ref.current?.click()}
                          >
                            <i className="cs-icon icon-camera"></i>
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div
                      className="file-upload-ui"
                      onClick={() => ref.current?.click()}
                    >
                      <i className="cs-icon icon-upload-file"></i>
                      <span>Upload Image</span>
                    </div>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    ref={ref}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleImageUpload(e.target.files[0]);
                      }
                      e.target.value = '';
                    }}
                  />
                </div>
              </div>
              <div className="edit-modal-fields">
                <div className="grid grid--2 grid--sm-mob--1">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={form.first_name}
                      onChange={handleFormChange}
                      className={submitted && error.first_name ? 'error' : ''}
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={form.last_name}
                      onChange={handleFormChange}
                      className={submitted && error.last_name ? 'error' : ''}
                    />
                  </div>
                  <div
                    className="form-group"
                    title={
                      isEdit && form.relation === 'guest'
                        ? 'Guest relation cannot be edited'
                        : ''
                    }
                  >
                    <label>Relation *</label>
                    <Select
                      placeholder="Select relation"
                      options={relationOptions}
                      classNamePrefix="cs-select"
                      value={
                        relationOptions.find(
                          (opt) => opt.value === form.relation
                        ) || null
                      }
                      onChange={(selectedOption) => {
                        if (isEdit && form.relation === 'guest') {
                          return;
                        }
                        handleFormChange({
                          target: {
                            name: 'relation',
                            value: selectedOption ? selectedOption.value : '',
                          },
                        });
                      }}
                      isClearable
                      isSearchable={false}
                      className={submitted && error.relation ? 'error' : ''}
                      isDisabled={isEdit && form.relation === 'guest'}
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth *</label>
                    <DatePicker
                      onChangeRaw={(e: any) => e.preventDefault()}
                      selected={
                        form.date_of_birth ? new Date(form.date_of_birth) : null
                      }
                      maxDate={new Date()}
                      onChange={(date) =>
                        handleFormChange({
                          target: {
                            name: 'date_of_birth',
                            value: date,
                          },
                        })
                      }
                      dateFormat="MM/dd/yyyy"
                      placeholderText="Select date"
                      showMonthDropdown
                      showYearDropdown
                      openToDate={
                        form.date_of_birth ? undefined : new Date(2010, 0, 1)
                      }
                      className={
                        submitted && error.date_of_birth ? 'error' : ''
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email (Optional)</label>
                    <input
                      id="email"
                      name="email"
                      value={form.email}
                      onChange={handleFormChange}
                      className={
                        isDuplicateEmail || (submitted && error.email)
                          ? 'error'
                          : ''
                      }
                    />
                    {(isDuplicateEmail || (submitted && error.email)) && (
                      <span className="error-message">
                        {isDuplicateEmail
                          ? 'Email already exists'
                          : error.email}
                      </span>
                    )}
                  </div>
                </div>
                <div className="form-actions buttons-group span-full justify-start">
                  <button
                    type="submit"
                    className="btn btn--sm btn--pri"
                    disabled={isImageUploading}
                  >
                    SAVE
                  </button>
                  <button
                    type="button"
                    className="btn btn--sm btn--sec"
                    onClick={handleClose}
                    disabled={isImageUploading}
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubMemberEditModal;
